import { ReactNode, useState } from 'react';
import { IconBack } from '@belong/icons';
import { Button, ButtonBase } from '@belong/ui';
import { ApiError } from 'src/utils/build-api-client';
import {
  PreQualificationForm,
  PreQualificationFormValues,
} from './components/pre-qualification-form';
import { PreQualificationResult } from './components/pre-qualification-result';
import { useCreateResidentPreQualification } from './queries';
import { RESIDENT_PRE_QUALIFICATION_STRINGS as STRINGS } from './resident-pre-qualification.strings';
import {
  ResidentPreQualificationIntent,
  ResidentPreQualificationModel,
} from './types';

function getErrorMessage(error: unknown) {
  if (!error) {
    return undefined;
  }

  return error instanceof ApiError && error.error
    ? error.error
    : STRINGS['form.error'];
}

type Props = {
  homeId: string;
  intent: ResidentPreQualificationIntent;
  ctaLabel: string;
  notQualifiedCtaLabel?: string;
  isProceeding?: boolean;
  initialPreQualification?: ResidentPreQualificationModel | null;
  canProceedWhenUnavailable: boolean;
  onProceed: () => void;
  onBack?: () => void;
  footer?: ReactNode;
};

export function ResidentPreQualification({
  homeId,
  intent,
  ctaLabel,
  notQualifiedCtaLabel,
  isProceeding,
  initialPreQualification,
  canProceedWhenUnavailable,
  onProceed,
  onBack,
  footer,
}: Props) {
  const [preQualification, setPreQualification] = useState(
    initialPreQualification,
  );
  const {
    trigger: createPreQualification,
    isMutating,
    error,
  } = useCreateResidentPreQualification();
  async function handleSubmit({
    annualHouseholdIncome,
    creditScoreBand,
  }: PreQualificationFormValues) {
    const created = await createPreQualification({
      homeId,
      intent,
      reportedAnnualHouseholdIncome: Number(annualHouseholdIncome),
      reportedCreditScoreBand: creditScoreBand,
    });

    if (created) {
      setPreQualification(created);
    }
  }

  return (
    <div className="w-full px-sm pb-2xl pt-2sm md:px-xl">
      {onBack && (
        <div className="absolute left-xs top-xs z-20 rounded bg-white p-xs opacity-80">
          <ButtonBase
            onClick={onBack}
            aria-label={STRINGS.back}
            className="block"
          >
            <IconBack width={16} className="text-navy" />
          </ButtonBase>
        </div>
      )}
      <div aria-live="polite">
        {preQualification && (
          <PreQualificationResult
            preQualification={preQualification}
            ctaLabel={ctaLabel}
            notQualifiedCtaLabel={notQualifiedCtaLabel}
            isProceeding={isProceeding}
            onProceed={onProceed}
          />
        )}
      </div>
      {!preQualification && (
        <>
          <PreQualificationForm
            isSubmitting={isMutating}
            errorMessage={getErrorMessage(error)}
            onSubmit={handleSubmit}
          />
          {!!error && canProceedWhenUnavailable && (
            <div className="mt-sm flex justify-center">
              <Button variant="text" onClick={onProceed}>
                {ctaLabel}
              </Button>
            </div>
          )}
        </>
      )}
      {!!footer && <div className="mt-xl flex justify-center">{footer}</div>}
    </div>
  );
}
